import { useState, useEffect } from "react";
import Navbar from "./Component/Navbar";
import emailjs from "@emailjs/browser";

function App() {
  const [theme, setTheme] = useState("light");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedService, setSelectedService] = useState(null);

  const testimonials = [
    {
      name: "Sarah Williams",
      role: "Startup Founder",
      rating: 5,
      message:
        "TechSolutions delivered our website exactly as promised. Communication was clear, timelines were respected, and the final product was fast, modern, and scalable. Highly reliable team.",
      image: "https://ui-avatars.com/api/?name=Sarah+Williams&background=991b1b&color=fff",
    },
    {
      name: "Michael Brown",
      role: "Business Analyst",
      rating: 5,
      message:
        "Their data analytics support helped us turn raw data into clear dashboards using Power BI. The insights we gained directly improved our decision-making process.",
      image: "https://ui-avatars.com/api/?name=Michael+Brown&background=991b1b&color=fff",
    },
    {
      name: "Emily Johnson",
      role: "Marketing Manager",
      rating: 4,
      message:
        "We saw a noticeable improvement in traffic and lead quality after working with their digital marketing team. SEO and campaign strategy were handled professionally.",
      image: "https://ui-avatars.com/api/?name=Emily+Johnson&background=991b1b&color=fff",
    },
    {
      name: "David Lee",
      role: "Product Manager",
      rating: 5,
      message:
        "From planning to deployment, the development process was smooth. The team understood our requirements clearly and delivered a high-quality web application.",
      image: "https://ui-avatars.com/api/?name=David+Lee&background=991b1b&color=fff",
    },
    {
      name: "Anita Sharma",
      role: "Small Business Owner",
      rating: 5,
      message:
        "As a non-technical founder, I appreciated how they explained everything in simple terms. The CRM solution they built saved us hours of manual work every week.",
      image: "https://ui-avatars.com/api/?name=Anita+Sharma&background=991b1b&color=fff",
    },
  ];

  const services = [
    {
      title: "Web Development",
      description:
        "Build responsive, scalable, and modern websites using React, Next.js, and full-stack solutions.",
      icon: "💻",
      details: [
        "Custom React & Next.js applications",
        "Responsive UI/UX design",
        "API & backend integration",
        "Performance optimization",
        "SEO-friendly architecture",
      ],
    },
    {
      title: "CRM Software Development",
      description:
        "Automate workflows, manage leads, track sales, and improve productivity.",
      icon: "📊",
      details: [
        "Custom CRM solutions",
        "Sales & lead automation",
        "Role-based dashboards",
        "Cloud-based scalable systems",
        "Integration with email & analytics",
      ],
    },
    {
      title: "Digital Marketing",
      description:
        "Enhance online presence with SEO, social media campaigns, and PPC.",
      icon: "📈",
      details: [
        "Search Engine Optimization (SEO)",
        "Social media marketing",
        "Google & Meta ads",
        "Email marketing campaigns",
        "Brand growth strategy",
      ],
    },
    {
      title: "Data Science & Analytics",
      description:
        "Leverage predictive analytics and AI for smarter decisions.",
      icon: "🤖",
      details: [
        "Data cleaning & visualization",
        "Python, SQL, Power BI",
        "Predictive analytics",
        "Machine learning models",
        "Business intelligence dashboards",
      ],
    },
  ];

  const mapImage =
    "https://upload.wikimedia.org/wikipedia/commons/8/80/World_map_-_low_resolution.svg";

  // Auto-slide testimonials
  useEffect(() => {
    if (isPaused) return; // Pause on hover

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 4000); // 9 seconds

    return () => clearInterval(interval); // Clean up
  }, [isPaused, testimonials.length]);


  // Load theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("theme") || "light";
    setTheme(savedTheme);
    document.documentElement.classList.toggle("dark", savedTheme === "dark");
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    document.documentElement.classList.toggle("dark", newTheme === "dark");
    localStorage.setItem("theme", newTheme);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    emailjs
      .sendForm(
        "service_fk7c7ma",
        "template_9acmq8q",
        e.target,
        "A3T_fu39_AzzA-8Xy"
      )
      .then(
        () => {
          alert("Message sent successfully!");
          e.target.reset();
        },
        () => {
          alert("Failed to send message. Please try again.");
        }
      );
  };

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${theme === "dark" ? "bg-gray-900 text-white" : "bg-white text-gray-900"
        }`}
    >
      {/* Navbar */}
      <Navbar theme={theme} toggleTheme={toggleTheme} />

      {/* Hero Section */}
      <section
        id="home"
        className="relative bg-cover bg-center bg-no-repeat h-screen flex items-center justify-center"
        style={{
          backgroundImage:
            theme === "dark"
              ? "url('https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1950&q=80')"
              : "url('https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1950&q=80')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative text-center text-white px-4">
          <h1 className="text-4xl md:text-6xl font-bold mb-4">
            Innovative Tech Solutions
          </h1>
          <p className="text-lg md:text-2xl mb-6">
            Web Development | CRM Software Development | Digital Marketing | Data
            Science
          </p>
          <a
            href="#services"
            className="inline-block px-6 py-3 bg-red-700 hover:bg-red-800 rounded-full font-semibold transition-colors duration-300"
          >
            Explore Services
          </a>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4 max-w-7xl mx-auto">
        <div className="md:flex md:space-x-12 flex-col md:flex-row">
          <div className="md:w-1/3 mb-10 md:mb-0">
            <h2 className="text-4xl font-bold mb-6">
              Explore Our <br />
              <span className="text-red-700">Tech Services</span>
            </h2>
            <p className="text-gray-700 dark:text-gray-300 text-lg">
              At EC Experts INC, we provide cutting-edge technology services to
              empower your business. From web development to CRM systems,
              digital marketing, and data science, our solutions are tailored to
              bridge the gap between your business goals and the digital world.
            </p>
          </div>

          <div className="md:w-2/3 grid gap-6 sm:grid-cols-2">
            {services.map((service, idx) => (
              <div
                key={idx}
                className={`rounded-lg shadow-lg p-6 transition-transform transform hover:scale-105 ${theme === "dark"
                  ? "bg-gray-800 text-white hover:bg-gray-700"
                  : "bg-white text-gray-900 hover:bg-red-50"
                  }`}
              >
                <div className="w-10 h-10 rounded-full bg-red-700 text-white flex items-center justify-center mb-2">
                  <span role="img" aria-label="icon">
                    {service.icon}
                  </span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{service.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {service.description}
                </p>
                <button
                  onClick={() => setSelectedService(service)}
                  className="mt-4 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-800 transition-colors duration-300"
                >
                  View More
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 px-4 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <img
              src="https://images.unsplash.com/photo-1557804506-669a67965ba0?auto=format&fit=crop&w=800&q=80"
              alt="Team collaborating"
              className="rounded-lg shadow-lg w-full"
            />
          </div>
          <div>
            <p className="text-sm text-red-700 dark:text-red-400 font-medium mb-2">
              About Our Company
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Empowering Businesses with{" "}
              <span className="text-red-700 dark:text-red-400">
                Digital Solutions
              </span>
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-8 text-lg">
              EC Experts INC delivers comprehensive digital services designed to
              help businesses grow and succeed. From web & software development,
              digital marketing, data analysis, to AI-powered insights, we
              bridge the gap between technology and business needs.
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {/* Testimonials Section */}
      <section
        id="testimonials"
        className="py-20 px-4 bg-gray-50 dark:bg-gray-950"
      >
        <div className="max-w-7xl mx-auto text-center mb-12">
          <p className="text-sm text-red-700 dark:text-red-400 font-medium mb-2">
            Trusted by Clients & Professionals
          </p>
          <h2 className="text-4xl md:text-5xl font-bold">
            What People Say About Us
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-4 max-w-2xl mx-auto">
            Real feedback from businesses and professionals who partnered with EC Experts INC.
          </p>
        </div>

        <div
          className="relative max-w-3xl mx-auto"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Testimonial Card */}
          {testimonials.map((t, idx) => (
            <div
              key={idx}
              className={`transition-opacity duration-1000 ${idx === currentIndex ? "opacity-100 relative" : "opacity-0 absolute top-0 left-0 w-full"
                }`}
            >
              <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 flex flex-col items-center text-center border border-gray-100 dark:border-gray-700 min-h-[400px]">
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-20 h-20 rounded-full mb-4 border-2 border-red-700"
                />
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
                  {t.role}
                </p>
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      className={`text-lg ${i < t.rating ? "text-yellow-400" : "text-gray-300 dark:text-gray-600"}`}
                    >
                      ★
                    </span>
                  ))}
                </div>
                <p className="text-gray-700 dark:text-gray-300 text-base leading-relaxed">
                  “{t.message}”
                </p>
              </div>
            </div>
          ))}

          {/* Dots */}
          <div className="flex justify-center mt-8 space-x-3">
            {testimonials.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${currentIndex === idx ? "bg-red-700 scale-125" : "bg-gray-300 dark:bg-gray-600 hover:bg-red-400"
                  }`}
                aria-label={`Go to testimonial ${idx + 1}`}
              />
            ))}
          </div>
        </div>
      </section>




      {/* ===================== CONTACT ===================== */}
      <section id="contact" className="py-20 px-4 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-12 flex-col md:flex-row items-center">
          {/* Contact Form */}
          <div>
            <p className="text-sm text-red-700 dark:text-red-400 font-medium mb-2">
              Get in Touch
            </p>
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Contact <span className="text-red-700 dark:text-red-400">EC Experts INC</span>
            </h2>
            <p className="text-gray-700 dark:text-gray-300 mb-8 text-lg">
              Have a project in mind or want to learn more? Fill out the form below.
            </p>

            <form className="grid gap-6" onSubmit={handleSubmit}>
              <input
                type="text"
                name="name"
                placeholder="Your Name"
                required
                className="p-3 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-700"
              />
              <input
                type="email"
                name="email"
                placeholder="Your Email"
                required
                className="p-3 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-700"
              />
              <textarea
                name="message"
                placeholder="Your Message"
                rows="5"
                required
                className="p-3 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-red-700"
              ></textarea>
              <button
                type="submit"
                className="px-6 py-3 bg-red-700 hover:bg-red-800 text-white rounded font-semibold transition-colors duration-300"
              >
                Send Message
              </button>
            </form>
          </div>

          {/* Map + Contact Info */}
          <div className="relative w-full h-96 rounded-lg overflow-hidden shadow-lg mt-10 md:mt-0">
            <img src={mapImage} alt="Map" className="w-full h-full object-cover" />
            <div
              className="absolute w-6 h-6 bg-red-600 rounded-full border-2 border-white shadow-lg"
              style={{ top: "45%", left: "51%", transform: "translate(-50%, -50%)" }}
            ></div>
            <div className="absolute bottom-6 left-6 bg-white dark:bg-gray-800 bg-opacity-90 dark:bg-opacity-90 p-4 rounded shadow-md">
              <p className="font-semibold text-gray-900 dark:text-gray-200">EC Experts INC</p>
              <p className="text-gray-700 dark:text-gray-300 text-sm">Nottingham, United Kingdom</p>
              <p className="text-gray-700 dark:text-gray-300 text-sm">jagpreet1399@gmail.com</p>
              <p className="text-gray-700 dark:text-gray-300 text-sm">+44 7831285723</p>
            </div>
          </div>
        </div>
      </section>

      {selectedService && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm px-4"
          onClick={() => setSelectedService(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-lg w-full p-6 md:p-8 animate-fadeIn"
          >
            {/* Header */}
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {selectedService.title}
                </h3>
                <p className="text-red-700 mt-1 font-medium">
                  Professional Service Overview
                </p>
              </div>
              <button
                onClick={() => setSelectedService(null)}
                className="text-gray-500 hover:text-red-700 text-2xl"
              >
                &times;
              </button>
            </div>

            {/* Description */}
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              {selectedService.description}
            </p>

            {/* Features */}
            <ul className="space-y-3 mb-6">
              {selectedService.details.map((item, i) => (
                <li
                  key={i}
                  className="flex items-center text-gray-700 dark:text-gray-300"
                >
                  <span className="w-2 h-2 bg-red-700 rounded-full mr-3"></span>
                  {item}
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setSelectedService(null)}
                className="px-4 py-2 rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-red-700"
              >
                Close
              </button>
              <a
                href="#contact"
                onClick={() => setSelectedService(null)}
                className="px-5 py-2 bg-red-700 hover:bg-red-800 text-white rounded font-semibold"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer
        className={`py-8 mt-12 text-center transition-colors duration-300 ${theme === "dark" ? "bg-gray-800 text-white" : "bg-gray-200 text-gray-900"
          }`}
      >
        <p>&copy; {new Date().getFullYear()} EC Experts INC. All rights reserved.</p>
        <p>Based in Nottingham, UK | jagpreet1399@gmail.com | +44 7831285723</p>
      </footer>
    </div>
  );
}

export default App;
